import React from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import { Close } from '@mui/icons-material';

interface ViewItem {
  item_id: string;
  parent_item_id?: string | null;
  seq_no?: number;
  data_index?: string;
  label_text?: string;
  renderer?: string;
  width?: number | null;
  pinned?: number | null;
  role?: string;
  extra_props?: Record<string, any>;
  [key: string]: any;
}

interface ColumnDetailPanelProps {
  row: ViewItem | null;
  onClose: () => void;
}

const CORE_FIELDS: { key: keyof ViewItem; label: string }[] = [
  { key: 'item_id', label: 'Item ID' },
  { key: 'data_index', label: 'Data Index' },
  { key: 'label_text', label: 'Label Text' },
  { key: 'renderer', label: 'Renderer' },
  { key: 'width', label: 'Width' },
  { key: 'pinned', label: 'Pinned' },
  { key: 'role', label: 'Role' },
  { key: 'seq_no', label: 'Seq No' },
];

const labelSx = {
  fontSize: 10,
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
  marginBottom: '2px',
};

const valueSx = {
  fontSize: 13,
  color: '#333',
  marginBottom: '12px',
  wordBreak: 'break-all' as const,
};

const emptyValueSx = {
  ...valueSx,
  color: '#CCC',
};

const formatValue = (val: any): React.ReactNode => {
  if (val === null || val === undefined || val === '') {
    return <Typography sx={emptyValueSx}>—</Typography>;
  }

  if (typeof val === 'object') {
    return (
      <Box
        sx={{
          fontFamily: 'monospace',
          fontSize: 11,
          background: '#F5F5F5',
          padding: '4px 8px',
          borderRadius: '4px',
          marginBottom: '12px',
          wordBreak: 'break-all',
          color: '#333',
        }}
      >
        {JSON.stringify(val, null, 2)}
      </Box>
    );
  }

  return <Typography sx={valueSx}>{String(val)}</Typography>;
};

const ColumnDetailPanel: React.FC<ColumnDetailPanelProps> = ({ row, onClose }) => {
  if (!row) return null;

  const extraProps = row.extra_props;
  const hasExtraProps = extraProps && typeof extraProps === 'object' && Object.keys(extraProps).length > 0;

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        borderLeft: '1px solid #E0E0E0',
        background: '#FAFAFA',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #E0E0E0',
          background: 'white',
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
          Column Details
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ padding: '4px' }}>
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
        {/* Core fields */}
        {CORE_FIELDS.map(({ key, label }) => (
          <Box key={key}>
            <Typography sx={labelSx}>{label}</Typography>
            {formatValue(row[key])}
          </Box>
        ))}

        {/* Extra Props */}
        {hasExtraProps && (
          <>
            <Divider sx={{ marginY: '8px' }} />
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: '10px' }}
            >
              Extra Props
            </Typography>
            {Object.entries(extraProps!).map(([propKey, propVal]) => (
              <Box key={propKey}>
                <Typography sx={labelSx}>{propKey}</Typography>
                {formatValue(propVal)}
              </Box>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ColumnDetailPanel;
